import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { message } from "antd";

const StateContext = createContext();


export const ContextProvider = ({ children }) => {
    const [activeMenu, setActiveMenu] = useState(false);
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('accessTokenOfUserApp'));
    const [showBackdrop, setShowBackdrop] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [lastVerification, setLastVerification] = useState(0);
    const [isVerifying, setIsVerifying] = useState(false);
    const [activeModule, setActiveModule] = useState(null);
    const [users, setUsers] = useState([]);
    
    // Referință pentru a stoca promisiunea verificării în curs
    const verificareInCursRef = useRef(null);

    const fetchUsers = useCallback(async () => {
        if (!token) return;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users?token=${token}`, {
                method: 'GET',  
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {      
                const data = await response.json(); 
                setUsers(data);
            } else {
                console.error('Eroare la încărcarea utilizatorilor:', response.statusText);
            }
        } catch (error) {
            console.error('Eroare la încărcarea utilizatorilor:', error);
        }
    }, [token]);        


    const verificaUtilizator = useCallback(async (forceRefresh = false) => {
        // Dacă există o verificare în curs, returnăm promisiunea existentă
        if (verificareInCursRef.current && !forceRefresh) {
            return verificareInCursRef.current;
        }
        
        // Verificăm timpul scurs de la ultima verificare
        const currentTime = Date.now();
        if (!forceRefresh && currentTime - lastVerification < 10000 && user) {
            return Promise.resolve(user);
        }
        
        // Creăm o nouă promisiune pentru verificare
        verificareInCursRef.current = (async () => {
            setIsVerifying(true);
            setLastVerification(currentTime);
            
            // Verificăm token-ul din state, dacă nu există, verificăm în localStorage
            const currentToken = token || localStorage.getItem('accessTokenOfUserApp');
            
            if (!currentToken) {
                setIsVerifying(false);
                verificareInCursRef.current = null;
                return null;
            }
            
            try {
                const requestOptions = {
                    method: "POST",
                    headers: { "Content-Type": "application/json"},
                    body: JSON.stringify({token: currentToken}),
                };

                const response = await fetch(`${process.env.REACT_APP_API_URL}/usergettoken`, requestOptions);    
                const data = await response.json();

                if (response.ok) {
                    // Dacă token-ul din state nu era setat, îl setăm acum
                    if (!token && currentToken) {
                        setToken(currentToken);
                    }
                    
                    setUser(data);
                    
                    return data;
                } else {
                    console.error("Eroare la verificarea utilizatorului:", data);
                    if (response.status === 401) {
                        removeToken(); // Token invalid sau expirat
                    }
                    return null;
                }
            } catch (error) {
                console.error("Eroare la verificarea utilizatorului:", error);
                return null;
            } finally {
                setIsVerifying(false);
                // Resetăm referința după finalizare
                verificareInCursRef.current = null;
            }
        })();
        
        return verificareInCursRef.current;
    }, [token, user, lastVerification]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]); 

    const saveToken = (token) => {
        localStorage.setItem('accessTokenOfUserApp', token);
        setToken(token);
    }

    const removeToken = () => {
        localStorage.removeItem('accessTokenOfUserApp');
        setToken(null);
        setUser(null);
    }

    return (
        <StateContext.Provider value={{
            activeMenu,
            setActiveMenu,
            token,
            saveToken,
            removeToken,
            user,
            setUser,
            isLoading,
            showBackdrop,
            setShowBackdrop,
            verificaUtilizator,
            activeModule,
            users
        }}>
            {children}
        </StateContext.Provider>
    )
    
}

export const useStateContext = () => useContext(StateContext);
